import { A11yModule } from '@angular/cdk/a11y';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategyOrigin,
  OverlayModule,
} from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';

import { SharedButtonComponent } from '@plugins/client/button/shared-button';
import { CancelIconComponent } from '@plugins/client/icons/cancel-icon';
import { TranslatePipe } from '@plugins/client/translate/pipes';
import type { Maybe } from '@plugins/shared/types';

import { FocusContainerDirective } from '../focus-container.directive';
import { FocusOutsideDirective } from '../focus-outside.directive';
import { OverlayTranslationModule } from '../translation-loader';

/**  Display angular cdk overlay with provided ng-content and an optional close button.
 *
 * Angular overlay should be available (these are included in `libs/client/styles/src/public/index.scss`)
 *
 * Example:
  ```html
  <button
   (click)="show = !show"
   cdkOverlayOrigin
   pluginsFocusOutside
    #focusOutside="pluginsFocusOutside"
    #origin="cdkOverlayOrigin">
   Toggle overlay
  </button>
  <plugins-overlay-container
    pluginsFocusContainer
    [focusOutsideDirectiveRef]="focusOutside"
    [origin]="origin"
    [showList]="show" >
  <plugins-user-list [userIds]="userIds" />
</plugins-overlay-container>
 ```
 */
@Component({
  selector: 'plugins-overlay-container',
  imports: [
    OverlayModule,
    A11yModule,
    SharedButtonComponent,
    CancelIconComponent,
    FocusContainerDirective,
    OverlayTranslationModule,
    TranslatePipe,
  ],
  templateUrl: './overlay-container.component.html',
  styleUrl: './overlay-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverlayContainerComponent {
  /** Origin relative to which to position the overlay.*/
  readonly origin = input.required<
    CdkOverlayOrigin | FlexibleConnectedPositionStrategyOrigin
  >();
  readonly show = input<boolean>(false);
  readonly maxWidth = input<string, string | undefined>('250px', {
    transform: (val) =>
      val ? `min(100vh - 20px, ${val})` : 'calc(100vh - 20px)',
  });
  readonly maxHeight = input<string, string | undefined>('300px', {
    transform: (val) =>
      val ? `min(100vw - 20px, ${val})` : 'calc(100vw - 20px)',
  });
  readonly autofocus = input(true);

  readonly showClose = input(false);
  /**  Determines overlay position in relation with origin. */
  readonly positions = input([
    new ConnectionPositionPair(
      { originX: 'end', originY: 'top' },
      { overlayX: 'start', overlayY: 'top' },
      10,
    ),
    new ConnectionPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'bottom' },
      10,
    ),
    new ConnectionPositionPair(
      { originX: 'end', originY: 'top' },
      { overlayX: 'end', overlayY: 'bottom' },
      0,
      -10,
    ),
    new ConnectionPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'end', overlayY: 'top' },
      0,
      10,
    ),
  ]);

  /** Determines if any margins should be respected by the overlay. Defaults to `10`. */
  readonly viewportMargin = input(10);
  /** If the modal should be closed when it loses focus,
   * the appropriate directive ref should be passed here. */
  readonly focusOutsideDirectiveRef = input<
    FocusOutsideDirective | undefined,
    Maybe<FocusOutsideDirective>
  >(undefined, {
    transform: (val: Maybe<FocusOutsideDirective>) =>
      val === null ? undefined : val,
  });

  /** Emits when user click optional close button. */
  readonly closed = output();

  readonly overlay = viewChild<CdkConnectedOverlay>(CdkConnectedOverlay);
  readonly wrapperDiv =
    viewChild<ElementRef<HTMLDivElement>>('componentWrapper');

  readonly hasScrollbar = computed(() => {
    const div = this.wrapperDiv();
    if (!this.show() || !div) return false;
    return div.nativeElement.clientWidth < div.nativeElement.offsetWidth;
  });

  public readonly id = `overlay-container-${Math.random()
    .toString(32)
    .slice(2)}`;

  handleClose() {
    this.closed.emit();
    this.focusOutsideDirectiveRef()?.blur(true);
  }

  handleContentChange() {
    if (this.show()) {
      this.overlay()?.overlayRef.updatePosition();
    }
  }
}
